import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import api from "../lib/api";
import LawyerSilhouette from "../components/LawyerSilhouette";
import { ArrowUpRight } from "lucide-react";
import useSiteContent from "../lib/useSiteContent";

export default function HomePage() {
  const { t, lang } = useLang();
  const [services, setServices] = useState([]);
  const [team, setTeam] = useState([]);
  const home = useSiteContent("home", {});
  const pick = (k, fallback) => home[`${k}_${lang}`] || fallback;

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100vh] bg-[#081A33] overflow-hidden ap-grain">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-55"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_alaca-partners/artifacts/waw3l7r2_sitenin%20arka%20plan%C4%B1.jpeg')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#081A33] via-[#081A33]/85 to-[#081A33]/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#081A33]/30 via-transparent to-[#081A33]/70" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-60 pb-24 lg:pt-72 lg:pb-32">
          <div className="max-w-3xl">
            <div className="overline text-[#C4A47C] ap-rise ap-rise-1">{pick("hero_eyebrow", t.hero.eyebrow)}</div>
            <h1
              data-testid="hero-title"
              className="font-serif-ap text-[#F3F5F8] text-5xl sm:text-6xl lg:text-[88px] leading-[0.98] tracking-tight mt-6 ap-rise ap-rise-2"
            >
              {pick("hero_title", t.hero.title)}
            </h1>
            <p className="text-[#F3F5F8]/75 text-lg lg:text-xl mt-10 max-w-xl leading-relaxed ap-rise ap-rise-3">
              {pick("hero_subtitle", t.hero.subtitle)}
            </p>
            <div className="mt-12 flex flex-wrap gap-4 ap-rise ap-rise-4">
              <Link
                to="/iletisim"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-[#C4A47C] text-[#0F2746] hover:bg-[#F3F5F8] transition-all px-7 py-4 text-sm tracking-wider uppercase font-semibold"
              >
                {t.hero.cta_primary}
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link
                to="/hizmetler"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 border border-[#F3F5F8]/40 text-[#F3F5F8] hover:border-[#F3F5F8] px-7 py-4 text-sm tracking-wider uppercase"
              >
                {t.hero.cta_secondary}
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 right-10 hidden md:flex flex-col items-end gap-2 text-[#F3F5F8]/50 text-[10px] tracking-[0.3em] uppercase">
          <span>41.0214° N</span>
          <span>29.0044° E</span>
          <span className="font-serif-ap text-base normal-case tracking-normal text-[#C4A47C]">Kız Kulesi</span>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-[#F6F8FA] py-24 lg:py-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <div className="overline text-[#4A6A8F]">{t.intro.eyebrow}</div>
            <div className="mt-8 h-[1px] w-16 bg-[#C4A47C]" />
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-serif-ap text-4xl lg:text-6xl leading-[1.05] text-[#0F2746] tracking-tight">
              {pick("intro_title", t.intro.title)}
            </h2>
            <p className="mt-10 text-[#3A5170] text-lg leading-relaxed max-w-2xl">
              {pick("intro_body", t.intro.body)}
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#EEF1F5] py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
            <div>
              <div className="overline text-[#4A6A8F]">{t.services.eyebrow}</div>
              <h2 className="font-serif-ap text-4xl lg:text-6xl text-[#0F2746] mt-4 max-w-2xl leading-[1.05]">
                {t.services.title}
              </h2>
            </div>
            <Link to="/hizmetler" className="overline text-[#0F2746] ap-link-underline" data-testid="services-cta">
              {t.services.cta} →
            </Link>
          </div>
          <div className="border-t border-[#0F2746]/15">
            {services.slice(0, 6).map((s, idx) => (
              <Link
                key={s.id}
                to={`/hizmetler#${s.slug}`}
                data-testid={`service-row-${s.slug}`}
                className="grid grid-cols-12 gap-6 py-8 border-b border-[#0F2746]/15 group hover:bg-[#0F2746]/5 transition-colors px-2"
              >
                <div className="col-span-2 lg:col-span-1 font-serif-ap text-2xl text-[#C4A47C]">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="col-span-10 lg:col-span-5 font-serif-ap text-2xl lg:text-3xl text-[#0F2746] leading-tight">
                  {lang === "tr" ? s.title_tr : s.title_en}
                </div>
                <div className="col-span-12 lg:col-span-5 text-[#3A5170] text-sm lg:text-base leading-relaxed">
                  {lang === "tr" ? s.summary_tr : s.summary_en}
                </div>
                <div className="hidden lg:flex col-span-1 items-center justify-end text-[#0F2746] group-hover:translate-x-1 transition-transform">
                  <ArrowUpRight size={20} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM PREVIEW */}
      <section className="bg-[#F6F8FA] py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-end mb-14 flex-wrap gap-4">
            <div>
              <div className="overline text-[#4A6A8F]">{t.team.eyebrow}</div>
              <h2 className="font-serif-ap text-4xl lg:text-6xl text-[#0F2746] mt-4 max-w-2xl leading-[1.05]">
                {t.team.title}
              </h2>
            </div>
            <Link to="/ekip" className="overline text-[#0F2746] ap-link-underline" data-testid="team-cta">
              {t.team.cta} →
            </Link>
          </div>
          <div className="border-t border-[#0F2746]/15">
            {team.slice(0, 4).map((m) => (
              <Link
                key={m.id}
                to={`/ekip/${m.id}`}
                data-testid={`team-card-${m.id}`}
                className="grid grid-cols-12 gap-6 py-7 border-b border-[#0F2746]/15 group hover:bg-[#0F2746]/5 transition-colors px-2"
              >
                <div className="col-span-12 lg:col-span-5 font-serif-ap text-2xl lg:text-3xl text-[#0F2746] leading-tight group-hover:text-[#C4A47C] transition-colors">
                  {m.name}
                </div>
                <div className="col-span-10 lg:col-span-6 overline text-[11px] text-[#4A6A8F] self-center">
                  {lang === "tr" ? m.title_tr : m.title_en}
                </div>
                <div className="hidden lg:flex col-span-1 items-center justify-end text-[#0F2746] group-hover:translate-x-1 transition-transform">
                  <ArrowUpRight size={18} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT BANNER (Bosphorus night image + CTA strip) */}
      <section className="relative w-full overflow-hidden bg-[#081A33]" data-testid="home-bottom-banner">
        <div className="aspect-[2/1] sm:aspect-[5/2] lg:aspect-[3/1] w-full relative">
          <img
            src={
              lang === "en"
                ? "https://customer-assets.emergentagent.com/job_alaca-partners/artifacts/sgev7wkc_law%20firm.jpeg"
                : "https://customer-assets.emergentagent.com/job_alaca-partners/artifacts/v08yb1y0_ChatGPT%20Image%203%20May%202026%2022_25_12.png"
            }
            alt="Alaca Partners — Boğaziçi"
            className="absolute inset-0 w-full h-full object-cover"
            draggable="false"
          />
        </div>

        {/* CTA strip below the image */}
        <div className="bg-[#081A33] border-t border-[#C4A47C]/20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 lg:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 text-[#F3F5F8]">
              <div className="overline text-[#C4A47C]">{t.contact.eyebrow}</div>
              <h2 className="font-serif-ap text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05]">
                {t.contact.title}
              </h2>
              <p className="text-[#F3F5F8]/70 mt-4 max-w-xl">{t.contact.subtitle}</p>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <Link
                to="/iletisim"
                data-testid="home-contact-cta"
                className="inline-flex items-center gap-3 bg-[#C4A47C] text-[#0F2746] hover:bg-[#F3F5F8] transition-all px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold"
              >
                {t.hero.cta_primary} <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
